import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api-client';
import { allocatedDownpaymentKey } from './use-allocated-downpayment';
import { blOrderInvoicesKey } from './use-bl-order-invoices';
import { invoicePaymentsKey } from './use-invoice-payments';

export interface DeletePaymentMutationArgs {
  readonly invoiceId: string;
  readonly orderId: string;
  readonly blOrderId?: string | undefined;
}

export interface DeletePaymentVariables {
  readonly paymentId: string;
  readonly delete_reason: string;
}

function invalidatePaymentQueries(
  qc: ReturnType<typeof useQueryClient>,
  args: DeletePaymentMutationArgs,
): void {
  void qc.invalidateQueries({ queryKey: invoicePaymentsKey(args.invoiceId) });
  void qc.invalidateQueries({ queryKey: ['order-advance-invoices', args.orderId] });
  void qc.invalidateQueries({ queryKey: ['order-invoices', args.orderId] });
  if (args.blOrderId !== undefined) {
    void qc.invalidateQueries({ queryKey: blOrderInvoicesKey(args.blOrderId) });
    void qc.invalidateQueries({ queryKey: allocatedDownpaymentKey(args.blOrderId) });
    void qc.invalidateQueries({ queryKey: ['bl-order-detail'] });
  }
}

export function useDeletePaymentMutation(args: DeletePaymentMutationArgs) {
  const qc = useQueryClient();
  return useMutation<undefined, Error, DeletePaymentVariables>({
    mutationFn: async ({ paymentId, delete_reason }) => {
      await apiRequest<undefined>(
        `/invoices/${encodeURIComponent(args.invoiceId)}/payments/${encodeURIComponent(paymentId)}`,
        { method: 'DELETE', body: { delete_reason } },
      );
      return undefined;
    },
    onSuccess: () => {
      invalidatePaymentQueries(qc, args);
    },
  });
}
